import { useMetaData } from './useMetaData.js'
import { useCommentsOp } from '../mutations/useCommentsOp.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useComments — read + mutate comment threads for a given context label.
 *
 * A thread is keyed by `contextLabel` (the goal or sub-goal name), matching
 * the current /q2-goals.html dashboard's storage format.
 *
 * Shape:
 *   meta.comments = [{
 *     id, contextLabel, contextType, resolved,
 *     replies: [{ id, author, text, at, attachments? }]
 *   }]
 *
 * Pass `contextLabel = null` to read/mutate ALL threads (useful for a
 * global inbox view later).
 *
 * Concurrency model: every mutation goes through /meta/comments which
 * does a server-side read-modify-write on the comments slice only.
 * Two teammates posting in different threads at the same time no
 * longer clobber each other's replies.
 */
export function useComments(contextLabel) {
  const currentUser = useUIStore((s) => s.currentUser) || 'Anonymous'
  const { data: meta } = useMetaData()
  const op = useCommentsOp()

  const all = Array.isArray(meta.comments) ? meta.comments : []
  const threads = contextLabel
    ? all.filter((t) => t.contextLabel === contextLabel)
    : all

  const newId = () => Math.random().toString(36).slice(2)

  const createThread = ({ text, contextType = '' }) => {
    if (!text?.trim() || !contextLabel) return
    const thread = {
      id: newId(),
      contextLabel,
      contextType,
      resolved: false,
      replies: [
        {
          id: newId(),
          author: currentUser,
          text: text.trim(),
          at: new Date().toISOString(),
        },
      ],
    }
    op.mutate({
      op: 'createThread',
      thread,
      optimistic: (current) => [...current, thread],
    })
  }

  const addReply = (threadId, text) => {
    if (!text?.trim()) return
    const reply = {
      id: newId(),
      author: currentUser,
      text: text.trim(),
      at: new Date().toISOString(),
    }
    op.mutate({
      op: 'addReply',
      threadId,
      reply,
      optimistic: (current) =>
        current.map((t) =>
          t.id === threadId
            ? { ...t, replies: [...(t.replies || []), reply] }
            : t
        ),
    })
  }

  const setResolved = (threadId, resolved) => {
    const resolvedAt = resolved ? new Date().toISOString() : null
    const resolvedBy = resolved ? currentUser : null
    op.mutate({
      op: 'setResolved',
      threadId,
      resolved,
      resolvedBy,
      resolvedAt,
      optimistic: (current) =>
        current.map((t) =>
          t.id === threadId ? { ...t, resolved, resolvedAt, resolvedBy } : t
        ),
    })
  }

  const deleteThread = (threadId) => {
    op.mutate({
      op: 'deleteThread',
      threadId,
      optimistic: (current) => current.filter((t) => t.id !== threadId),
    })
  }

  // Stats for the bubble: count of open threads and total replies under them
  const openThreads = threads.filter((t) => !t.resolved)
  const openReplyCount = openThreads.reduce(
    (n, t) => n + (t.replies?.length || 0),
    0
  )

  return {
    threads,
    openThreads,
    openThreadCount: openThreads.length,
    openReplyCount,
    createThread,
    addReply,
    setResolved,
    deleteThread,
  }
}
